import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import { ThemeOverlay } from "./ThemeOverlay";
import { resolveCardTheme } from "./theme";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type PartnerPitchCardContent,
  type PitchExtraPage,
} from "./types";

interface PartnerPitchCardProps {
  format: CardFormat;
  content: PartnerPitchCardContent;
  /** Which slide to render. 0 = cover (default). 1+ = extraPages[index-1]. */
  pageIndex?: number;
}

const PartnerPitchCard = forwardRef<HTMLDivElement, PartnerPitchCardProps>(
  function PartnerPitchCard({ format, content, pageIndex = 0 }, ref) {
    const isStory = format === "story";
    const style = content.style ?? {};
    const themeId = content.theme ?? "collab";
    const theme = resolveCardTheme(themeId, {
      monoColor: content.monoColor,
      partnerColor: content.partnerColor,
    });

    const extras = content.extraPages ?? [];
    const totalPages = 1 + extras.length;
    const safeIndex = Math.min(Math.max(pageIndex, 0), totalPages - 1);
    const isCover = safeIndex === 0;
    const chapter: PitchExtraPage | undefined = isCover
      ? undefined
      : extras[safeIndex - 1];

    // Per-chapter background overrides the card-level Background tab when set.
    const pageBackground = chapter?.backgroundImageUrl
      ? { imageUrl: chapter.backgroundImageUrl, opacity: style.background?.opacity ?? 0.3 }
      : style.background;

    const eyebrowStyle: CSSProperties = {
      ...resolveTextStyle(
        style.eyebrow,
        {
          fontFamily: theme.fontEyebrow,
          fontSizePx: 22,
          color: theme.accentColor,
          letterSpacing: "0.4em",
          bold: true,
        },
        format,
      ),
      textTransform: "uppercase",
      marginTop: isStory ? 16 : 8,
    };

    const headlineDefault = isStory ? 132 : 104;
    const headlineStyle: CSSProperties = {
      ...resolveTextStyle(
        style.headline,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: headlineDefault,
          color: theme.headlineColor,
          letterSpacing: "0.005em",
          bold: false,
        },
        format,
      ),
      lineHeight: 0.9,
      marginTop: isStory ? 24 : 16,
    };

    const goldStyle: CSSProperties = {
      ...resolveTextStyle(
        style.goldHighlight,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: headlineDefault,
          color: theme.accentColor,
        },
        format,
      ),
      display: "block",
      lineHeight: 0.9,
    };

    const ctaStyle: CSSProperties = {
      ...resolveTextStyle(
        style.ctaText,
        {
          fontFamily: theme.fontBody,
          fontSizePx: isStory ? 26 : 22,
          color: theme.ctaFg,
          bold: true,
        },
        format,
      ),
      display: "inline-flex",
      alignItems: "center",
      gap: 12,
      marginTop: isStory ? 40 : 28,
      padding: isStory ? "20px 36px" : "16px 30px",
      borderRadius: 9999,
      background: theme.ctaBg,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      boxShadow: `0 20px 50px ${theme.ctaBg}40`,
      alignSelf: "flex-start",
    };

    const sectionLabel: CSSProperties = {
      fontFamily: theme.fontEyebrow,
      fontSize: isStory ? 16 : 14,
      letterSpacing: "0.35em",
      textTransform: "uppercase",
      color: `${theme.accentColor}cc`,
      fontWeight: 800,
      marginBottom: isStory ? 16 : 12,
    };

    const stepNumStyle: CSSProperties = {
      fontFamily: theme.fontHeadline,
      fontSize: isStory ? 56 : 44,
      color: theme.accentColor,
      lineHeight: 1,
      flexShrink: 0,
      width: isStory ? 56 : 44,
    };

    const stepTextStyle: CSSProperties = {
      ...resolveTextStyle(
        style.body,
        {
          fontFamily: theme.fontBody,
          fontSizePx: isStory ? 28 : 22,
          color: theme.headlineColor,
        },
        format,
      ),
      lineHeight: 1.35,
      flex: 1,
    };

    const benefitStyle: CSSProperties = {
      ...resolveTextStyle(
        style.body,
        {
          fontFamily: theme.fontBody,
          fontSizePx: isStory ? 26 : 20,
          color: theme.subtleTextColor,
        },
        format,
      ),
      lineHeight: 1.5,
    };

    // Small "n / N" chip shown on every page when the deck has more than 1 slide.
    const pageChip = totalPages > 1 && (
      <div
        style={{
          position: "absolute",
          top: isStory ? 24 : 18,
          right: isStory ? 28 : 22,
          zIndex: 4,
          display: "inline-flex",
          alignItems: "center",
          whiteSpace: "nowrap",
          width: "fit-content",
          fontFamily: theme.fontBody,
          fontSize: isStory ? 14 : 12,
          fontWeight: 800,
          letterSpacing: "0.18em",
          color: `${theme.accentColor}cc`,
          padding: "6px 14px",
          borderRadius: 9999,
          background: "rgba(0,0,0,0.35)",
          border: `1px solid ${theme.accentColor}40`,
        }}
      >
        {String(safeIndex + 1).padStart(2, "0")} / {String(totalPages).padStart(2, "0")}
      </div>
    );

    // Chapter-only styles, computed once and reused inside the chapter branch.
    const chapterEyebrowStyle: CSSProperties = {
      ...resolveTextStyle(
        style.eyebrow,
        {
          fontFamily: theme.fontEyebrow,
          fontSizePx: 24,
          color: theme.accentColor,
          letterSpacing: "0.4em",
          bold: true,
        },
        format,
      ),
      textTransform: "uppercase",
      marginTop: isStory ? 80 : 48,
    };

    const chapterHeadlineDefault = isStory ? 132 : 100;
    const chapterHeadlineStyle: CSSProperties = {
      ...resolveTextStyle(
        style.headline,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: chapterHeadlineDefault,
          color: theme.headlineColor,
          letterSpacing: "0.005em",
        },
        format,
      ),
      lineHeight: 0.95,
      marginTop: isStory ? 24 : 16,
    };

    const chapterGoldStyle: CSSProperties = {
      ...resolveTextStyle(
        style.goldHighlight,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: chapterHeadlineDefault,
          color: theme.accentColor,
        },
        format,
      ),
      display: "block",
      lineHeight: 0.95,
    };

    const chapterBodyStyle: CSSProperties = {
      ...resolveTextStyle(
        style.body,
        {
          fontFamily: theme.fontBody,
          fontSizePx: isStory ? 32 : 26,
          color: theme.subtleTextColor,
        },
        format,
      ),
      lineHeight: 1.55,
      marginTop: isStory ? 40 : 28,
      whiteSpace: "pre-wrap",
    };

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("PARTNER_PITCH")}
        footerRight={content.hashtags || "#FBKForBusiness · #FBK"}
        header={content.header}
        background={pageBackground}
        customBaseGradient={theme.customBaseGradient}
        headerOnLight={theme.headerOnLight}
        suppressBrandOverlays={theme.suppressBrandOverlays}
        hideFooter={content.footer?.hide}
      >
        <ThemeOverlay theme={theme} format={format} />
        {pageChip}

        {/* CHAPTER LAYOUT (pageIndex > 0) — simpler info/walkthrough layout. */}
        {!isCover && chapter && (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "row",
              gap: isStory ? 48 : 32,
              position: "relative",
              zIndex: 2,
              minHeight: 0,
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minWidth: 0,
              }}
            >
              <div style={chapterEyebrowStyle}>{chapter.eyebrow}</div>
              <div style={chapterHeadlineStyle}>
                {chapter.headline}
                {chapter.goldHighlight && (
                  <span style={chapterGoldStyle}>{chapter.goldHighlight}</span>
                )}
              </div>
              {chapter.body && <div style={chapterBodyStyle}>{chapter.body}</div>}
              {chapter.ctaText && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 12,
                    marginTop: isStory ? 40 : 28,
                    padding: isStory ? "20px 36px" : "16px 30px",
                    borderRadius: 9999,
                    background: theme.ctaBg,
                    color: theme.ctaFg,
                    fontFamily: theme.fontBody,
                    fontSize: isStory ? 26 : 22,
                    fontWeight: 800,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    boxShadow: `0 20px 50px ${theme.ctaBg}40`,
                    alignSelf: "flex-start",
                  }}
                >
                  {chapter.ctaText} →
                </div>
              )}
            </div>

            {chapter.imageUrl && (
              <div
                style={{
                  width: isStory ? 380 : 340,
                  flexShrink: 0,
                  alignSelf: "center",
                }}
              >
                <img
                  src={chapter.imageUrl}
                  alt=""
                  crossOrigin={
                    chapter.imageUrl.startsWith("data:") ? undefined : "anonymous"
                  }
                  style={{
                    display: "block",
                    width: "100%",
                    height: isStory ? 980 : 660,
                    maxHeight: isStory ? "calc(100vh - 200px)" : 660,
                    objectFit: "contain",
                    filter:
                      "drop-shadow(0 30px 60px rgba(0,0,0,0.55)) drop-shadow(0 12px 24px rgba(0,0,0,0.4))",
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* COVER LAYOUT (pageIndex === 0) — original hook design. */}
        {isCover && (
          <>
        {/* Co-brand bar: FBK ✕ Partner */}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: isStory ? 24 : 18,
            paddingTop: isStory ? 24 : 16,
            paddingBottom: isStory ? 16 : 10,
          }}
        >
          <div
            style={{
              fontFamily: theme.fontHeadline,
              fontSize: isStory ? 48 : 38,
              color: "#FFFFFF",
              letterSpacing: "0.04em",
            }}
          >
            FBK
          </div>
          <div
            style={{
              fontFamily: theme.fontBody,
              fontSize: isStory ? 36 : 28,
              color: theme.accentColor,
              fontWeight: 200,
              opacity: 0.9,
            }}
          >
            ✕
          </div>
          {content.partnerLogoUrl ? (
            <img
              src={content.partnerLogoUrl}
              alt=""
              crossOrigin={
                content.partnerLogoUrl.startsWith("data:") ? undefined : "anonymous"
              }
              style={{
                height: isStory ? 56 : 44,
                width: "auto",
                maxWidth: isStory ? 280 : 220,
                objectFit: "contain",
                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
              }}
            />
          ) : (
            <div
              style={{
                fontFamily: theme.fontHeadline,
                fontSize: isStory ? 48 : 38,
                color: "#FFFFFF",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              {content.partnerName || "PARTNER"}
            </div>
          )}
        </div>

        {/* MAIN content column */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            position: "relative",
            zIndex: 2,
            paddingTop: isStory ? 16 : 10,
            minHeight: 0,
          }}
        >
          <div style={eyebrowStyle}>{content.eyebrow}</div>

          <div style={headlineStyle}>
            {content.headline}
            {content.goldHighlight && (
              <span style={goldStyle}>{content.goldHighlight}</span>
            )}
          </div>

          {/* HOW IT WORKS */}
          {content.steps.length > 0 && (
            <div
              style={{
                marginTop: isStory ? 40 : 28,
                padding: isStory ? "28px 30px" : "22px 24px",
                borderRadius: 20,
                background: "rgba(0,0,0,0.28)",
                backdropFilter: "blur(8px)",
                border: `1px solid ${theme.accentColor}40`,
              }}
            >
              <div style={sectionLabel}>How it works</div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: isStory ? 16 : 12,
                }}
              >
                {content.steps.slice(0, 3).map((s, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: isStory ? 16 : 12,
                    }}
                  >
                    <div style={stepNumStyle}>
                      {["①", "②", "③"][i] ?? `${i + 1}.`}
                    </div>
                    <div style={stepTextStyle}>{s.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* WHY IT WORKS */}
          {content.benefits.length > 0 && (
            <div style={{ marginTop: isStory ? 32 : 22 }}>
              <div style={sectionLabel}>Why it works</div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: isStory ? 10 : 8,
                }}
              >
                {content.benefits.map((b, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 14,
                    }}
                  >
                    <span
                      style={{
                        color: theme.accentColor,
                        fontSize: isStory ? 26 : 22,
                        lineHeight: 1,
                        fontWeight: 800,
                      }}
                    >
                      ✓
                    </span>
                    <span style={benefitStyle}>{b.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {content.ctaText && <div style={ctaStyle}>{content.ctaText} →</div>}
        </div>
          </>
        )}
      </FbkCardShell>
    );
  },
);

export default PartnerPitchCard;
